import { useEffect, useRef, useState, type RefObject } from 'react'
import { Box, ButtonBase, CardMedia, Stack, Typography } from '@mui/material'
import { Add, RestaurantMenu } from '@mui/icons-material'
import type { ProdutoCatalogo } from '../api'
import { capitalize } from '../lib/formato'
import { urlImagemProduto } from '../lib/imagem'
import { PrecoProduto } from './preco-produto'

interface PecaTambemProps {
  produtos: ProdutoCatalogo[]
  aoAdicionar: (produto: ProdutoCatalogo) => void
}

/** Faixa "Peça também" do carrinho: bebidas e sobremesas que entram no pedido com um toque. */
export function PecaTambem({ produtos, aoAdicionar }: PecaTambemProps): JSX.Element | null {
  const carrossel = useRef<HTMLDivElement>(null)
  const { paginas, atual, irPara } = usePaginasCarrossel(carrossel, produtos.length)

  if (produtos.length === 0) return null

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
        Pode ser que você goste
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Acompanhamentos que combinam com seu pedido
      </Typography>

      <Box
        ref={carrossel}
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          // Mesmo carrossel dos "Destaques": encaixa cada card ao parar de arrastar, sem scrollbar.
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {produtos.map((produto) => (
          <Box
            key={produto.id}
            // No celular o último card fica cortado (sinal de que rola); da tela média para cima cabem 4.
            sx={{ flex: '0 0 auto', width: { xs: 136, sm: 'calc((100% - 36px) / 4)' }, scrollSnapAlign: 'start' }}
          >
            <CardPecaTambem produto={produto} aoAdicionar={aoAdicionar} />
          </Box>
        ))}
      </Box>

      {paginas > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 1.5 }}>
          {Array.from({ length: paginas }, (_, pagina) => (
            <ButtonBase
              key={pagina}
              onClick={() => irPara(pagina)}
              aria-label={`Ver sugestões, parte ${pagina + 1} de ${paginas}`}
              aria-current={pagina === atual ? 'true' : undefined}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: pagina === atual ? 'primary.main' : 'grey.300',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function CardPecaTambem({
  produto,
  aoAdicionar,
}: {
  produto: ProdutoCatalogo
  aoAdicionar: (produto: ProdutoCatalogo) => void
}): JSX.Element {
  const nome = capitalize(produto.descricao)
  const composicao = produto.descricaoComposicao?.trim()
  const imagem = produto.imagens.find((i) => i.preferencial) ?? produto.imagens[0]

  return (
    // O card inteiro é o botão: alvo de toque grande, e o "+" é só a indicação visual.
    <ButtonBase
      onClick={() => aoAdicionar(produto)}
      aria-label={`Adicionar ${nome}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '6 / 5', borderBottom: '1px solid', borderColor: 'divider' }}>
        {imagem ? (
          // Inteira e sem recorte; absoluta para foto em pé não esticar a caixa 6:5 (e, com ela, os cards da linha).
          <CardMedia
            component="img"
            image={urlImagemProduto(imagem, { largura: 280, altura: 232 })}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RestaurantMenu color="disabled" />
          </Box>
        )}

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Add fontSize="small" />
        </Box>
      </Box>

      <Box sx={{ p: 1.25, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {nome}
        </Typography>

        {composicao && (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
            {composicao}
          </Typography>
        )}

        {/* Preço ancorado na base: fica na mesma linha em todos os cards, com ou sem descrição. */}
        <PrecoProduto produto={produto} sx={{ mt: 'auto', pt: 1 }} valor={{ variant: 'body1', sx: { fontWeight: 700 } }} />
      </Box>
    </ButtonBase>
  )
}

/** Quantas "telas" o carrossel tem e em qual o cliente está: alimenta as bolinhas embaixo dele. */
function usePaginasCarrossel(
  ref: RefObject<HTMLDivElement | null>,
  quantidade: number,
): { paginas: number; atual: number; irPara: (pagina: number) => void } {
  const [paginas, setPaginas] = useState(1)
  const [atual, setAtual] = useState(0)

  useEffect(() => {
    const elemento = ref.current
    if (!elemento) return

    const medir = (): void => {
      const { clientWidth, scrollWidth, scrollLeft } = elemento
      // Sem largura ainda (ou no jsdom dos testes): fica uma página só, e as bolinhas não aparecem.
      if (clientWidth === 0) return

      const total = Math.max(1, Math.ceil(scrollWidth / clientWidth - 0.05))
      const chegouAoFim = scrollLeft >= scrollWidth - clientWidth - 2
      setPaginas(total)
      setAtual(chegouAoFim ? total - 1 : Math.round(scrollLeft / clientWidth))
    }

    medir()
    elemento.addEventListener('scroll', medir, { passive: true })
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir)
    observador?.observe(elemento)

    return () => {
      elemento.removeEventListener('scroll', medir)
      observador?.disconnect()
    }
  }, [ref, quantidade])

  const irPara = (pagina: number): void => {
    const elemento = ref.current
    elemento?.scrollTo({ left: pagina * elemento.clientWidth, behavior: 'smooth' })
  }

  return { paginas, atual, irPara }
}
